import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Header from "@/components/Header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Loader2 } from "lucide-react";

const MAX_ATTEMPTS = 20;
const POLL_INTERVAL = 2000;

export default function PaymentSuccess() {
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let timeoutId;

    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setStatus("paid");
          refresh();
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus("timeout");
          return;
        }
        attemptsRef.current += 1;
        timeoutId = setTimeout(poll, POLL_INTERVAL);
      } catch {
        if (!cancelled) setStatus("error");
      }
    };
    poll();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen">
      <Header inApp />
      <div className="max-w-md mx-auto px-4 py-16">
        <Card className="tactile-card p-8 rounded-md text-center" data-testid="payment-success-card">
          {status === "polling" && (
            <>
              <Loader2 className="h-12 w-12 mx-auto animate-spin text-[#00E676]" />
              <h1 className="font-display text-2xl font-bold mt-4">Confirming payment…</h1>
              <p className="text-sm text-[#A0A7B0] mt-2">This takes a couple seconds.</p>
            </>
          )}
          {status === "paid" && (
            <>
              <div className="w-16 h-16 mx-auto rounded-full bg-[#00E676]/15 border border-[#00E676]/40 grid place-items-center">
                <Check className="h-8 w-8 text-[#00E676]" />
              </div>
              <div className="font-mono text-[11px] uppercase tracking-widest text-[#00E676] mt-4">MEMBERSHIP ACTIVE</div>
              <h1 className="font-display text-3xl font-bold mt-1 tracking-tight">You're clawing back.</h1>
              <p className="text-sm text-[#A0A7B0] mt-2">
                We'll scan your inbox nightly and hit you when a trial is about to convert.
              </p>
              <Link to="/dashboard">
                <Button className="btn-emerald mt-6 w-full h-11" data-testid="success-back-dashboard">Back to dashboard</Button>
              </Link>
            </>
          )}
          {(status === "timeout" || status === "error") && (
            <>
              <h1 className="font-display text-2xl font-bold mt-2">Still processing</h1>
              <p className="text-sm text-[#A0A7B0] mt-2">Payment is confirming with Stripe. Refresh in a moment.</p>
              <Link to="/dashboard">
                <Button variant="outline" className="btn-outline-paper mt-6 w-full h-11">Back to dashboard</Button>
              </Link>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
